import Box from "@mui/material/Box";
import ReactEcharts from "echarts-for-react";
import { useEffect, useRef } from "react";
import { formatDateString } from "../../utils/timeUtils";

const KneeRangeMotionChart = ({ data, view, weekStart, weekEnd, month, year, dateStart, dateEnd, chartType }) => {
  const getDayOfWeek = (index) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[index % 7];
  };

  const getMonthName = (index) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[index % 12];
  };

  const adjustBarWidth = (dataLength) => {
    if (dataLength <= 12) {
      return 20;
    } else if (dataLength <= 20) {
      return 15;
    } else if (dataLength <= 40) {
      return 10;
    } else {
      return 5;
    }
  };


  const specificXAxisLabels = [1, 5, 10, 15, 20, 25, 30];
  let xAxisData;
  let yAxisData;
  switch (view) {
    case "month":
      xAxisData = data.map((item, index) => {
        if (specificXAxisLabels.includes(index + 1)) {
          return index + 1;
        } else {
          return "";
        }
      });
      yAxisData = data.map((item) => item.romKnee);
      break;
    case "year":
      // For yearly view, use month names
      xAxisData = data.map((_, index) => getMonthName(index));
      yAxisData = data.map((item) => {
        if (item.valid_days === 0 || item.romKnee === 0) {
          return 0;
        } else {
          return item.romKnee;
          // return (item.romKnee / item.valid_days).toFixed(2);
        }
      });
      break;
    case "custom":
      xAxisData = data.map((item) => item.date);
      yAxisData = data.map((item) => item.romKnee);
      break;
    default:
      xAxisData = data.map((_, index) => getDayOfWeek(index));
      yAxisData = data.map((item) => item.romKnee);
  }

  const chartRef = useRef(null);

  const getTitleText = () => {
    switch (view) {
      case "month":
        return "Knee Range Motion" + " [" + formatDateString(month, view) + "]";
      case "year":
        return "Knee Range Motion " + formatDateString(year, view);
      case "custom":
        return "Knee Range Motion " + "[" + formatDateString(dateStart, "date") + " - " + formatDateString(dateEnd, "date") + "]";
      default:
        return "Knee Range Motion " + "[" + formatDateString(weekStart, "date") + " - " + formatDateString(weekEnd, "date") + " " + weekStart.getFullYear() + "]";
    }
  };

  const option = {
    title: {
      text: getTitleText(),
      left: "center",
    },
    color: ["#9FD3D3"],
    tooltip: {
      trigger: "axis",
    },
    legend: {},
    xAxis: {
      type: "category",
      data: xAxisData || [],
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: {
        fontSize: 15,
      },
      axisLabel: {
        // rotate: 30,
        fontSize: 12, // Reduce font size if necessary
      },
      axisLine: {
        lineStyle: {
          color: "#000000",
        },
      },
    },
    yAxis: {
      type: "value",
      name: "degrees",
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: {
        fontSize: 15,
      },
      axisLine: {
        lineStyle: {
          color: "#000000",
        },
      },
    },
    calculable: true,
    toolbox: {
      show: true, // Set to true to enable the toolbox
      feature: {
        magicType: { show: true, type: ['line', 'bar'] },
        saveAsImage: {
          // Enable the "Save As Image" feature
          show: true, // Show the "Save As Image" icon
          title: "Save As Image", // Set the tooltip title for the "Save As Image" icon
          name: "knee_range_motion_chart", // Set the default filename for the downloaded image
          excludeComponents: ["toolbox"], // Exclude the toolbox icon from the downloaded image
          pixelRatio: 2, // Set the pixel ratio for the downloaded image (default is 1)
        },
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        if (view === "week" || view === "month" || view === "custom") {
          const fullDate = data[params.dataIndex].date;
          return `${fullDate}<br/>${params.seriesName}: ${params.value}`;
        } else {
          const month = getMonthName(params.dataIndex);
          return `${month}<br/>${params.seriesName}: ${params.value}`;
        }
      }
    },
    series: [
      {
        // name: "knee range motion",
        data: yAxisData || [],
        type: chartType,
        smooth: true,
        barMaxWidth: adjustBarWidth(data.length),
        lineStyle: {
          normal: {
            width: 4,
            type: "solid",
          },
        },
        areaStyle: {
          color: "#E9EFFD",
        },
        symbolSize: 8,
        label: {
          show: view === "month" || view === "custom" ? false : true,
          position: "top",
          textStyle: {
            fontSize: 15,
          },
        },
      },
    ],
  };

  useEffect(() => {
    const resizeListener = () => {
      if (chartRef.current) {
        chartRef.current.getEchartsInstance().resize();
      }
    };
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  return (
    <Box>
      <Box width="100%" height="300px" borderRadius={3} boxShadow={2} p={1}>
        <ReactEcharts
          ref={chartRef}
          option={option}
          style={{ height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default KneeRangeMotionChart;
